import { RefObject, useCallback, useEffect, useRef } from "react";

export function useChatScroll(containerRef: RefObject<HTMLDivElement>) {
    const messageRef = useRef<HTMLDivElement>(null);
    const isScrollingRef = useRef(false);
    const rafIdRef = useRef<number | null>(null);

    const scrollToBottomOnLoad = useCallback(() => {
        // this request should wait for the container element to render
        requestAnimationFrame(() => {
            // this request should wait for the markdown content to render (important for citations)
            requestAnimationFrame(() => {
                const container = containerRef.current;
                if (!container) {
                    return;
                }

                container.scrollTo({
                    top: container.scrollHeight,
                    behavior: "smooth",
                });
            });
        });
    }, [containerRef]);

    const scrollUserMessageToTop = useCallback(() => {
        const container = containerRef.current;
        const messageElement = messageRef.current;
        if (!container || !messageElement) return;

        const TOP_PADDING = 16;
        isScrollingRef.current = true;

        const scroll = () => {
            if (!isScrollingRef.current) return;

            const containerRect = container.getBoundingClientRect();
            const messageRect = messageElement.getBoundingClientRect();

            const offsetTop = messageRect.top - containerRect.top;

            if (offsetTop > TOP_PADDING) {
                container.scrollTop += offsetTop - TOP_PADDING;

                rafIdRef.current = requestAnimationFrame(scroll);
            } else {
                rafIdRef.current = null;
            }
        };

        rafIdRef.current = requestAnimationFrame(scroll);
    }, [containerRef, messageRef]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const handleUserScroll = () => {
            isScrollingRef.current = false;
            if (rafIdRef.current !== null) {
                cancelAnimationFrame(rafIdRef.current);
                rafIdRef.current = null;
            }
        };

        // Listen to user scroll events to interrupt automatic scrolling
        const scrollEvents = ["wheel", "touchstart", "keydown"];
        scrollEvents.forEach((event) => {
            container.addEventListener(event, handleUserScroll, {
                passive: true,
            });
        });

        return () => {
            scrollEvents.forEach((event) => {
                container.removeEventListener(event, handleUserScroll);
            });
            if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
        };
    }, [containerRef]);

    return {
        messageRef,
        scrollToBottomOnLoad,
        scrollUserMessageToTop,
    };
}
